<script setup lang="ts">
import {
  iButton,
  iModal,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
  iTextInput,
  iTextInputModes,
  openModal,
  closeModal,
} from '@inscribe/signal';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useConfigurableInsightsStore } from '@/V2/stores/pinia/decision-engine/configurable-insights.store';
import { toKebabCase } from '@/utils/string';

// Router
const router = useRouter();

// Store
const configurableInsightsStore = useConfigurableInsightsStore();

// type
interface InsightOption {
  title: string;
  description: string;
}

// Refs
const newInsightPrompt = ref<string>('');
const inputHasError = ref<boolean>(false);
const inputErrorText = ref<string>('');

// Computed
const insightGenerating = computed(() => configurableInsightsStore.isLoading);

// Insight options
const insightOptions: InsightOption[] = [
  {
    title: 'Multiple NSF Transactions',
    description:
      'Flag the customer if they have more than one transaction that was returned due to insufficient funds.',
  },
  {
    title: 'Large Cash Deposits',
    description:
      'Flag the customer if they make a cash deposit of ten thousand dollars or more.',
  },
  {
    title: 'High daily balance volatility',
    description:
      'Flag the customer if their account balance changes significantly over a thirty-day period.',
  },
  {
    title: 'Overdrawn balance',
    description:
      'Flag the customer if their account balance drops below zero at any point.',
  },
];

// Methods
function selectOption(option: InsightOption) {
  newInsightPrompt.value = option.description;
}

async function generateInsight() {
  inputHasError.value = false;
  inputErrorText.value = '';

  if (newInsightPrompt.value === '') {
    inputHasError.value = true;
    inputErrorText.value = 'Please enter a prompt';
    return;
  }

  try {
    const insight = await configurableInsightsStore.generateConfigurableInsight(
      newInsightPrompt.value,
    );
    configurableInsightsStore.setSelectedConfigurableInsight(insight); // needed so the edit page can access the insight
    closeModal('new-configuration-bank-insight-modal');
    router.push({ name: 'decision-engine-signals-edit' });
  } catch (error) {
    inputHasError.value = true;
    inputErrorText.value =
      configurableInsightsStore.getError || 'Failed to generate insight';
  }
}

function configureManually() {
  // Clear insight for new/manual configuration
  configurableInsightsStore.setSelectedConfigurableInsight(null);
  closeModal('new-configuration-bank-insight-modal');
  router.push({ name: 'decision-engine-signals-edit' });
}

defineExpose({
  openModal: () => {
    openModal('new-configuration-bank-insight-modal');
  },
  closeModal: () => {
    closeModal('new-configuration-bank-insight-modal');
  },
});
</script>

<template>
  <i-modal
    modal-id="new-configuration-bank-insight-modal"
    class="new-configuration-bank-insight-modal"
    data-testid="new-configurable-insight-modal"
    title="Transaction Insight"
    subtitle="Describe what you want to monitor"
    :confirm-button-text="insightGenerating ? 'Generating...' : 'Generate'"
    :confirm-button-loading="insightGenerating"
    :confirm-button-disabled="insightGenerating"
    cancel-button-text="Cancel"
    :close-on-submit="false"
    :submit-on-enter="true"
    @on-submit="generateInsight"
  >
    <template #body-content>
      <div
        class="options-wrapper d-flex flex-wrap i-gap-8"
        :class="{ 'is-generating': insightGenerating }"
      >
        <button
          v-for="option in insightOptions"
          :key="option.title"
          class="insight-option-card d-flex flex-column i-px-10 i-py-9 i-radius-3xl i-gap-3"
          :class="{ 'is-selected': newInsightPrompt === option.description }"
          :data-testid="`insight-option-${toKebabCase(option.title)}`"
          :data-dd-action-name="`insight-option-${toKebabCase(option.title)}`"
          @click="selectOption(option)"
        >
          <div class="title i-text-button-2">
            {{ option.title }}
          </div>
          <div class="description i-text-body-3">
            {{ option.description }}
          </div>
        </button>
      </div>

      <div class="separator d-flex i-my-12">
        <span class="separator-text i-px-6">or describe your own</span>
      </div>
      <i-text-input
        type="textarea"
        class="prompt-input"
        data-testid="insight-prompt-input"
        :class="{ 'is-generating': insightGenerating }"
        :disabled="insightGenerating"
        placeholder="e.g. Flag if customer has more than 5 gambling transactions"
        :mode="iTextInputModes.TEXTAREA"
        :rows="5"
        :model-value="newInsightPrompt"
        :has-error="inputHasError"
        :show-hint-text="inputHasError"
        :hint-text="inputErrorText"
        @update:model-value="newInsightPrompt = $event"
      />
    </template>

    <template #footer-content>
      <div class="button-wrapper">
        <i-button
          label="Configure Manually"
          :button-style="iButtonStyles.OUTLINE"
          :type="iButtonTypes.NEUTRAL"
          :size="iButtonSizes.LARGE"
          data-testid="configure-manually-button"
          data-dd-action-name="configure-manually-button"
          @button-click="configureManually"
        />
      </div>
    </template>
  </i-modal>
</template>

<style lang="scss">
.new-configuration-bank-insight-modal {
  &.i-modal .modal-content {
    width: 600px;
  }

  .button-wrapper {
    width: 300px;
  }

  .prompt-input {
    min-height: 150px;

    &.is-generating {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .separator {
    position: relative;

    span {
      background: var(--background-neutral-subtle);
      color: var(--text-neutral-subtle);
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
    }

    &:before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 1px;
      background: var(--border-neutral-subtle);
    }
  }

  .options-wrapper {
    display: flex;
    flex-wrap: wrap;

    &.is-generating {
      opacity: 0.5;
      pointer-events: none;
    }

    .insight-option-card {
      background: var(--background-neutral-subtle);
      flex: 0 0 calc(50% - 8px);
      height: 114px;
      border: 1px solid var(--border-neutral-subtle);
      cursor: pointer;
      text-align: left;
      transition: all 0.15s ease-in-out;

      .title {
        color: var(--text-neutral-bold);
      }
      .description {
        color: var(--text-neutral);
      }

      &.is-selected {
        background: var(--surface-neutral-subtle);
        border-color: var(--border-neutral);
      }

      &:hover {
        background: var(--surface-neutral-subtle);
        border-color: var(--border-neutral);
      }
    }
  }
}
</style>
